import React, { useState, useEffect } from "react";
import axios from "axios";
import { RefreshCw, Phone, Mail, Inbox, Lock } from "lucide-react";
import { IMAGES } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PASSCODE = "lightspeed"; // simple client-side gate for the demo dashboard

export default function AdminLeads() {
  const [authed, setAuthed] = useState(false);
  const [pass, setPass] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data || []);
    } catch (e) {
      setError("Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
  }, [authed]);

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-ls flex items-center justify-center px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (pass === PASSCODE) setAuthed(true);
            else setError("Wrong passcode.");
          }}
          className="w-full max-w-sm rounded-2xl p-8 text-center"
          style={{ background: "var(--ls-surface)", border: "1px solid var(--ls-border-strong)" }}
        >
          <img src={IMAGES.logo} alt="LightSpeed ROI" className="h-16 w-auto object-contain mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-orange" />
            <span className="font-cond font-bold uppercase tracking-wide text-white">Lead Dashboard</span>
          </div>
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Enter passcode"
            className="w-full px-4 py-3 rounded-[6px] text-white placeholder:text-[#7d7264] outline-none text-center"
            style={{ background: "#05070b", border: "1px solid var(--ls-border)" }}
          />
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
          <button type="submit" className="btn-orange w-full justify-center mt-4">
            Unlock
          </button>
          <p className="text-xs text-[#9a8f80] mt-4">Demo passcode: lightspeed</p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ls">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="LightSpeed ROI" className="h-12 w-auto object-contain" />
            <div>
              <h1 className="font-cond font-extrabold text-2xl text-white uppercase tracking-wide">Leads</h1>
              <p className="text-sm text-[#b7ab9b]">{leads.length} total</p>
            </div>
          </div>
          <button onClick={load} className="btn-ghost text-sm">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {error && <p className="text-red-400 mb-4">{error}</p>}

        {leads.length === 0 && !loading ? (
          <div className="text-center py-24 text-[#9a8f80]">
            <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No leads yet. They'll show up here the moment someone submits a form or shares a number in chat.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.map((l) => (
              <div key={l.id} className="card-dark p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-cond font-bold text-lg text-white">{l.name || "—"}</span>
                  {l.trade ? (
                    <span className="text-xs font-bold uppercase tracking-wide text-orange">{l.trade}</span>
                  ) : null}
                </div>
                <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-orange font-semibold mb-1">
                  <Phone className="w-4 h-4" /> {l.phone}
                </a>
                {l.email ? (
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-[#b7ab9b] text-sm mb-2">
                    <Mail className="w-4 h-4" /> {l.email}
                  </a>
                ) : null}
                {l.website ? <p className="text-sm text-[#b7ab9b] mb-2">🌐 {l.website}</p> : null}
                {l.message ? <p className="text-sm text-[#d7cdbf] leading-snug mb-3">{l.message}</p> : null}
                <div className="flex items-center justify-between text-xs text-[#9a8f80] pt-2 border-t" style={{ borderColor: "var(--ls-border)" }}>
                  <span className="uppercase tracking-wide">{(l.source || "website").replace(/_/g, " ")}</span>
                  <span>{fmt(l.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
